import React, { useEffect, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import KPITile from "@/components/shared/KPITile";
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";
import { Clock, CheckCircle2, Siren, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { downloadReport } from "@/lib/downloadReport";
import api, { extractApiError } from "@/lib/api";
import { toast } from "sonner";

function formatMinutes(valueInSeconds) {
  const numeric = Number(valueInSeconds);
  if (!Number.isFinite(numeric)) return "—";
  return `${Math.round(numeric / 60)} min`;
}

function formatDayLabel(isoDate) {
  if (!isoDate) return "";

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return String(isoDate);

  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

export default function PoliceReports() {
  const [incidentSummary, setIncidentSummary] = useState(null);
  const [casesPerDay, setCasesPerDay] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadAnalytics = async () => {
      setError("");

      try {
        const [summaryResponse, timeseriesResponse] = await Promise.all([
          api.get("/analytics/incidents/summary", { params: { period: "7d" } }),
          api.get("/analytics/incidents/timeseries", { params: { bucket: "day" } }),
        ]);

        if (!isMounted) return;

        setIncidentSummary(summaryResponse?.data?.data || {});
        setCasesPerDay(
          Array.isArray(timeseriesResponse?.data?.series)
            ? timeseriesResponse.data.series.map((item) => ({
                day: formatDayLabel(item?.bucket),
                cases: Number(item?.count || 0),
              }))
            : [],
        );
      } catch (err) {
        if (!isMounted) return;
        const { message } = extractApiError(err);
        setError(message || "Failed to load reports analytics.");
        setIncidentSummary(null);
        setCasesPerDay([]);
      }
    };

    loadAnalytics();

    return () => {
      isMounted = false;
    };
  }, []);

  const casesThisWeek = incidentSummary?.cases;
  const closed = incidentSummary?.closed;
  const avgResponseSeconds = incidentSummary?.avg_response_seconds;

  return (
    <div className="space-y-6" data-testid="police-reports">
      <PageHeader
        title="Reports"
        subtitle="Operational performance for your unit."
        onDownload={() => {
          downloadReport({
            filename: "police_weekly_report",
            title: "Police - Weekly Performance Report",
            headers: ["Day", "Cases"],
            rows: casesPerDay.map((d) => [d.day, d.cases]),
          });
          toast.success("Report downloaded");
        }}
        actions={
          <Button variant="outline" data-testid="export-btn"><Download size={14} className="mr-1.5" /> Export PDF</Button>
        }
      />

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPITile
          icon={Siren}
          label="Cases this week"
          value={Number.isFinite(Number(casesThisWeek?.value)) ? Number(casesThisWeek.value).toLocaleString() : "—"}
          accent="role"
        />
        <KPITile
          icon={CheckCircle2}
          label="Closed"
          value={Number.isFinite(Number(closed?.value)) ? Number(closed.value).toLocaleString() : "—"}
          accent="emerald"
        />
        <KPITile
          icon={Clock}
          label="Avg response"
          value={formatMinutes(avgResponseSeconds?.value)}
          accent="blue"
        />
        <KPITile
          icon={FileText}
          label="Reports filed"
          value={Number.isFinite(Number(closed?.value)) ? Number(closed.value).toLocaleString() : "—"}
          accent="indigo"
        />
      </div>

      <SectionCard title="Cases per day">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={casesPerDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Bar dataKey="cases" fill="#0369A1" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>
    </div>
  );
}
